import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Description from '../components/Description';
import ReCaptchaWrapper from '../components/ReCaptchaWrapper';

const CompanyPage = () => {
  const { companySlug } = useParams();
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setErrorMessage('');
        const response = await fetch(`https://api.thunai.ai/auth-service/ai/api/v1/thunai/customDomain/sharelink/get-widget?uniquerUserName=${companySlug}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          let serverMessage = '';
          try {
            const errorResult = await response.json();
            serverMessage = errorResult?.message || errorResult?.error || '';
          } catch (_) {
            // Ignore parsing errors and use a fallback message below.
          }

          if (response.status === 404) {
            throw new Error('Company page not found. Please check the URL and try again.');
          }

          throw new Error(
            serverMessage ||
            `Unable to load company details (${response.status}). Please try again later.`
          );
        }
        
        const result = await response.json();
        setCompanyData(result?.data || result);
        setErrorMessage('');
      } catch (error) {
        console.error('Error fetching company data:', error);
        setCompanyData(null);
        setErrorMessage(
          error?.message || 'Something went wrong while loading this company page.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [companySlug]);

  const displayData = companyData || {
    name: companySlug.toUpperCase(),
    widget_id: 'thunai-entrans-67d186f45addd646a4b08bd9',
    primary_color: '#08086E',
    secondary_color: '#2E2EBD',
    tertiary_color: '#FFFFFF'
  };

  const primaryColor = displayData.primary_color || '#08086E';
  const secondaryColor = displayData.secondary_color || '#2E2EBD';
  // const tertiaryColor = displayData.tertiary_color || '#FFFFFF';

  useEffect(() => {
    // Load external scripts
    const scripts = [
      { src: 'https://storage.googleapis.com/thunai-agent/thunai-voice-agent/vendor.js', type: 'module' },
      { src: 'https://storage.googleapis.com/thunai-agent/thunai-voice-agent/main.js', type: 'module' },
      { src: 'https://storage.googleapis.com/thunai-agent/thunai-chat-agent/vendor.js', type: 'module' },
      { src: 'https://storage.googleapis.com/thunai-agent/thunai-chat-agent/main.js', type: 'module' },
      { src: 'https://storage.googleapis.com/thunai-agent/common-agent/vendor.js', type: 'module' },
      { src: 'https://storage.googleapis.com/thunai-agent/common-agent/main.js', type: 'module' },
    ];

    const loadedScripts = [];

    scripts.forEach(({ src, type }) => {
      const script = document.createElement('script');
      script.src = src;
      script.type = type;
      document.body.appendChild(script);
      loadedScripts.push(script);
    });

    return () => {
      loadedScripts.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-gray-600" style={{ color: '#4b5563' }}>Loading...</div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 1.5rem', background: 'linear-gradient(to bottom, #f8fafc, #f1f5f9)' }}>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center" style={{ backgroundColor: '#fff', borderRadius: '1rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)', padding: '2rem', maxWidth: '32rem', width: '100%', textAlign: 'center' }}>
          <h1 className="text-2xl font-bold text-gray-800 mb-3" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.75rem' }}>
            Unable to load page
          </h1>
          <p className="text-gray-600" style={{ color: '#4b5563' }}>{errorMessage}</p>
        </div>
      </div>
    );
  }

  const title = displayData?.title || `${companySlug.toUpperCase()} | Thunai AI`;
  const description = displayData?.description || 
    `AI Voice Assistant for ${companySlug.toUpperCase()}. Talk to the assistant in real-time.`;

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100"
      style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #f8fafc, #f1f5f9)' }}
    >
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <link rel="icon" type="image/png" sizes="32x32" href="/images/favicon.png" />
      </Helmet>

      <div 
        className="relative overflow-hidden" 
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
        }}
      >
        <div
          className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAzNGM0LjQxOCAwIDgtMy41ODIgOC04cy0zLjU4Mi04LTgtOC04IDMuNTgyLTggOCAzLjU4MiA4IDggOHoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4wNSIvPjxwYXRoIGQ9Ik01MSAyOHY2SDQ1di02aDZ6TTE1IDIydjZIOXYtNmg2eiIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utb3BhY2l0eT0iLjA1Ii8+PHBhdGggZD0iTTMwIDQ0YzguODM3IDAgMTYtNy4xNjMgMTYtMTZzLTcuMTYzLTE2LTE2LTE2LTE2IDcuMTYzLTE2IDE2IDcuMTYzIDE2IDE2eiIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')]"
          style={{ position: 'absolute', inset: 0, opacity: 0.1 }}
        ></div>
        <div
          className="absolute -left-12 -top-12 w-40 h-40 rounded-full bg-white/10 blur-3xl"
          style={{ position: 'absolute', left: '-3rem', top: '-3rem', width: '10rem', height: '10rem', borderRadius: '9999px', backgroundColor: 'rgba(255,255,255,0.1)', filter: 'blur(64px)' }}
        ></div>
        <div
          className="absolute -right-12 -bottom-12 w-40 h-40 rounded-full bg-white/10 blur-3xl"
          style={{ position: 'absolute', right: '-3rem', bottom: '-3rem', width: '10rem', height: '10rem', borderRadius: '9999px', backgroundColor: 'rgba(255,255,255,0.1)', filter: 'blur(64px)' }}
        ></div>
      </div>

      <div className="max-w-6xl mx-auto px-6 -mt-8 md:-mt-12 relative z-10" style={{ maxWidth: '72rem', margin: '0 auto', padding: '0 1.5rem', marginTop: '-3rem', position: 'relative', zIndex: 10 }}>
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mt-[10%]" style={{ backgroundColor: '#fff', borderRadius: '1rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)', overflow: 'hidden', marginTop: '10%' }}>
          <div className="p-8 border-b border-gray-100" style={{ padding: '2rem', borderBottom: '1px solid #f3f4f6', background: `linear-gradient(135deg, ${primaryColor}15 0%, ${secondaryColor}15 100%)` }}>
            <div className="max-w-5xl" style={{ maxWidth: '64rem' }}>
              {displayData.title && (
                <h1 className=" " style={{ fontSize: '1.875rem', fontWeight: 800, color: '#111827' }}>
                  {displayData.title}
                </h1>
              )}
             
              {displayData.description && (
                <Description 
                  text={displayData.description} 
                  primaryColor={primaryColor}
                />
              )}
              
              <div className="flex items-center gap-4 text-sm text-gray-500 mt-8" style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.875rem', color: '#6b7280', marginTop: '2rem' }}>
                {displayData.uniquerUserName && (
                  <div className="flex items-center gap-2" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ height: '1.25rem', width: '1.25rem' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                    <span>{displayData.uniquerUserName}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid" style={{ display: 'grid' }}>
            <div className="lg:col-span-2 border-b lg:border-b-0 lg:border-r border-gray-100" style={{ borderBottom: '1px solid #f3f4f6', borderRight: '1px solid #f3f4f6' }}>
              {displayData.voice_widget_Id && displayData.voice_widget_Id !== '' ? (
                <div className="p-8 md:p-10" style={{ padding: '2.5rem' }}>
                  <div className="flex items-center justify-between mb-3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke={primaryColor} style={{ height: '1.5rem', width: '1.5rem' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                      Speak To Voice Assistant
                    </h2>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" style={{ display: 'inline-flex', alignItems: 'center', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 500, backgroundColor: '#dcfce7', color: '#166534' }}>
                      Live
                    </span>
                  </div>
                  <p className="text-gray-600 mb-6" style={{ color: '#4b5563', marginBottom: '1.5rem' }}>Start speaking or type your questions below to interact with our AI assistant.</p>
                  
                  <div className="bg-gray-50 rounded-xl border border-gray-100 flex justify-center items-center" style={{ backgroundColor: '#f9fafb', borderRadius: '0.75rem', border: '1px solid #f3f4f6', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <voice-agent 
                      widgetid={displayData.voice_widget_Id} 
                      className="w-full h-auto"
                      style={{ width: '100%', height: 'auto' }}
                    ></voice-agent>
                  </div>
                </div>
              ) : null}

              <div>
                <div>
                  {displayData.common_widget_Id !== '' ? (
                    <thunai-agent widgetid={displayData.common_widget_Id} className="w-full h-full" style={{ width: '100%', height: '100%' }}></thunai-agent>
                  ) : (
                    <chat-agent widgetid={displayData.chat_widget_Id} className="w-full h-full" style={{ width: '100%', height: '100%' }}></chat-agent>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {displayData.trademark && (
          <div className="text-center mt-8 mb-10 text-gray-500 text-sm" style={{ textAlign: 'center', marginTop: '2rem', marginBottom: '2.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
            Powered by <a 
              href="https://www.thunai.ai" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="font-medium hover:text-gray-700 transition inline-flex items-center gap-2"
              style={{ color: primaryColor, fontWeight: 500, transition: 'color 0.2s', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <img 
                src="/images/thunai-logo.svg" 
                alt="Thunai AI" 
                className="h-4 w-auto"
                style={{ height: '1rem', width: 'auto' }}
              />
            </a>
          </div>
        )}
      </div>

      <ReCaptchaWrapper />
    </div>
  );
};

export default CompanyPage;
